import type { Breadcrumb, ErrorEvent } from "@sentry/react";

// WI#25 — the post-purchase sign-in lands on /sign-in?__clerk_ticket=… (a live single-use Clerk
// sign-in ticket), and PeakCommerce links here with ?token=…. With sendDefaultPii on, the browser
// SDK attaches the page URL to events and records navigations/fetches as breadcrumbs, so scrub
// both params everywhere before anything is sent. Mirrors api-server/src/lib/sentryScrub.ts.
// WI#28: the ticket now arrives as /auth/complete#ticket=…, so the fragment is scrubbed too.
const SECRET_PARAMS = ["token", "ticket", "__clerk_ticket"];

// WI#45 — mirrors the server: no cookie or authorization value ever leaves for Sentry. The browser
// SDK doesn't attach them by default, but Clerk's __session cookie is script-readable, so strip
// them wherever an event or breadcrumb could carry them, as defence in depth.
const SECRET_HEADERS = new Set(["cookie", "set-cookie", "authorization", "proxy-authorization"]);
const HEADER_BAGS = new Set(["headers", "request_headers", "response_headers"]);

export function scrubHeaders<T extends Record<string, unknown>>(headers: T): T {
  for (const key of Object.keys(headers)) {
    const lower = key.toLowerCase();
    if (SECRET_HEADERS.has(lower)) delete headers[key];
    else if (lower === "referer" && typeof headers[key] === "string") {
      (headers as Record<string, unknown>)[key] = scrubUrl(headers[key] as string);
    }
  }
  return headers;
}

export function scrubUrl(value: string): string {
  let out = value;
  for (const p of SECRET_PARAMS) {
    out = out.replace(new RegExp(`([?&#]${p}=)[^&#]*`, "g"), "$1[Filtered]");
  }
  return out;
}

export function scrubSentryEvent(event: ErrorEvent): ErrorEvent {
  if (event.request) {
    if (typeof event.request.url === "string") event.request.url = scrubUrl(event.request.url);
    if (typeof event.request.query_string === "string") {
      event.request.query_string = scrubUrl(`?${event.request.query_string}`).slice(1);
    }
    delete event.request.cookies;
    if (event.request.headers) scrubHeaders(event.request.headers);
  }
  event.breadcrumbs = event.breadcrumbs?.map(scrubBreadcrumb);
  return event;
}

export function scrubBreadcrumb(crumb: Breadcrumb): Breadcrumb {
  if (typeof crumb.message === "string") crumb.message = scrubUrl(crumb.message);
  if (crumb.data) {
    for (const [k, v] of Object.entries(crumb.data)) {
      const lower = k.toLowerCase();
      if (SECRET_HEADERS.has(lower)) delete crumb.data[k];
      else if (HEADER_BAGS.has(lower) && v && typeof v === "object") scrubHeaders(v as Record<string, unknown>);
      else if (typeof v === "string") crumb.data[k] = scrubUrl(v);
    }
  }
  return crumb;
}
